import { useEffect, useEffectEvent, useId, useRef, useState, type FormEvent, type ReactNode } from "react";
import { useUnsavedChanges } from "@/shared/hooks/useUnsavedChanges";
import { Button, IconButton } from "../Button";
import { ErrorBanner } from "../feedback/Feedback";

export type ModalSize = "sm" | "md" | "lg" | "xl";

/** Only the last-opened dialog answers Escape (a confirmation stacked over an editor). */
const isTopmost = (el: HTMLElement | null) => {
  const dialogs = document.querySelectorAll(".modal[role=dialog]");
  return !!el && dialogs[dialogs.length - 1] === el;
};

export function Modal({ title, subtitle, open = true, onClose, children, footer, size = "md", dirty = false }: {
  title: string; subtitle?: ReactNode; open?: boolean; onClose: () => void; children: ReactNode; size?: ModalSize;
  /** A function receives the guarded close, for the footer's own Cancel button. */
  footer?: ReactNode | ((requestClose: () => void) => ReactNode);
  /** Unsaved edits: Esc, backdrop, × and Cancel ask before discarding them, and leaving the page is blocked. */
  dirty?: boolean;
}) {
  const titleId = useId();
  const ref = useRef<HTMLDivElement>(null);
  const [discarding, setDiscarding] = useState(false);
  useUnsavedChanges(open && dirty, "modal");
  const requestClose = () => (dirty ? setDiscarding(true) : onClose());
  // Callers pass inline closures; reading the latest one keeps the effect (and focus) put on re-render.
  const close = useEffectEvent(() => requestClose());

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && isTopmost(ref.current) && close();
    document.addEventListener("keydown", onKey);
    // Freeze the page behind the dialog.
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    // Move focus into the dialog; give it back on close.
    const previous = document.activeElement as HTMLElement | null;
    ref.current?.querySelector<HTMLElement>(".modal-body input:not([type=hidden]):not([type=radio]), .modal-body select, .modal-body textarea, .modal-footer .btn-primary")?.focus();
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = overflow;
      previous?.focus();
    };
  }, [open]);

  if (!open) return null;
  return (
    <>
      <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && requestClose()}>
        <div ref={ref} className={`modal modal-${size}`} role="dialog" aria-modal="true" aria-labelledby={titleId}>
          <header className="modal-header">
            <div className="stack flex-1">
              <h2 id={titleId}>{title}</h2>
              {subtitle && <p className="card-subtitle">{subtitle}</p>}
            </div>
            <IconButton label="Đóng" icon="x" onClick={requestClose} />
          </header>
          <div className="modal-body">{children}</div>
          {footer && <footer className="modal-footer">{typeof footer === "function" ? footer(requestClose) : footer}</footer>}
        </div>
      </div>
      {/* A sibling, not a child: the backdrop's blur would trap a nested fixed overlay inside the editor. */}
      {discarding && (
        <Modal title="Bỏ thay đổi chưa lưu?" size="sm" onClose={() => setDiscarding(false)} footer={<>
          <Button variant="ghost" autoFocus onClick={() => setDiscarding(false)}>Tiếp tục sửa</Button>
          <Button variant="danger" onClick={() => { setDiscarding(false); onClose(); }}>Bỏ thay đổi</Button>
        </>}>
          <p>Những gì bạn vừa nhập trong “{title}” chưa được lưu và sẽ mất.</p>
        </Modal>
      )}
    </>
  );
}

/**
 * Modal wrapping a <form>: Enter submits, the footer has Cancel + submit, and
 * the mutation error is shown on top.
 */
export function FormModal({ title, subtitle, onClose, onSubmit, submitLabel = "Lưu", pending, error, size, children, footerStart, dirty }: {
  title: string; subtitle?: ReactNode; onClose: () => void; onSubmit: () => void; submitLabel?: string; pending?: boolean;
  error?: unknown; size?: ModalSize; children: ReactNode;
  /** Extra content on the left of the footer (hints, secondary actions). */
  footerStart?: ReactNode;
  /** Unsaved edits: closing asks before discarding them (see Modal). */
  dirty?: boolean;
}) {
  const formId = useId();
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!pending) onSubmit();
  };
  return (
    <Modal title={title} subtitle={subtitle} onClose={onClose} size={size} dirty={dirty} footer={(requestClose) => <>
      {footerStart && <><div className="row gap-sm muted small">{footerStart}</div><span className="spacer" /></>}
      <Button variant="ghost" onClick={requestClose}>Huỷ</Button>
      <Button type="submit" form={formId} variant="primary" loading={pending}>{submitLabel}</Button>
    </>}>
      <form id={formId} className="stack gap-lg" onSubmit={submit} noValidate>
        <ErrorBanner error={error} />
        {children}
      </form>
    </Modal>
  );
}
